import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, useTheme, CircularProgress, Alert,
    TablePagination, Snackbar, Chip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Add, CloudUpload } from "@mui/icons-material";
import CreateGoal from './createGoal';
import EditGoal from './editGoal';

// Assuming you have your axiosInstance and endpoints configured
// import { getData, postData } from '../../../services/axiosInstance';
// import { API_ENDPOINTS } from '../../../services/endpoints';

function GoalManagement() {
    const theme = useTheme();
    const [openAddGoal, setOpenAddGoal] = useState(false);
    const [openEditGoal, setOpenEditGoal] = useState(false);
    const [editingGoal, setEditingGoal] = useState(null);
    const [goalsHistoryData, setGoalsHistoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [openExcelDialog, setOpenExcelDialog] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    const [excelUploading, setExcelUploading] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // --- Data Fetching ---
    const fetchGoals = async () => {
        setLoading(true);
        try {
            // Replace with your actual API call
            // const res = await getData(API_ENDPOINTS.getGoals(page, rowsPerPage));
            // if (res.success) {
            //     setGoalsHistoryData(res.data);
            //     setTotalCount(res.total);
            // }

            // Mock Data for demonstration since API is commented out
            const mockData = [
                { id: 1, title: "Quarterly Sales", employees: ["John Doe", "Jane Smith"], status: "Pending", createdDate: "2026-04-10T10:00:00Z" },
                { id: 2, title: "UI Refresh", employees: ["Narendra", "Prasanth"], status: "Completed", createdDate: "2026-04-08T14:30:00Z" }
            ];
            setGoalsHistoryData(mockData);
            setTotalCount(mockData.length);
            setError(null);
        } catch (err) {
            setError("Failed to load goals. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGoals();
    }, [page, rowsPerPage]);

    // --- Helper Functions ---
    const truncateName = (name) => {
        return name.length > 15 ? `${name.substring(0, 15)}...` : name;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed':
            case 'Accepted': return theme.palette.success.main;
            case 'Submitted a goal': return theme.palette.info.main;
            case 'Pending': return theme.palette.warning.main;
            case 'Rejected': return theme.palette.error.main;
            default: return theme.palette.grey[500];
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    // --- Event Handlers ---
    const handleAddHrGoal = () => setOpenAddGoal(true);

    const handleEditGoal = (goal) => {
        setEditingGoal(goal);
        setOpenEditGoal(true);
    };

    const handlePageChange = (event, newPage) => setPage(newPage);

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExcelFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) setExcelFile(file);
    };

    const handleCloseExcelDialog = () => {
        setOpenExcelDialog(false);
        setExcelFile(null);
    };

    const handleUploadExcel = async () => {
        if (!excelFile) return;
        setExcelUploading(true);
        try {
            // Logic for uploading file to API
            showSnackbar("File uploaded successfully!");
            handleCloseExcelDialog();
            fetchGoals();
        } catch (err) {
            showSnackbar("Upload failed", "error");
        } finally {
            setExcelUploading(false);
        }
    };

    // --- Components ---
    const EmployeeListDisplay = ({ employees = [] }) => {
        const displayCount = 2;
        const displayedEmployees = employees.slice(0, displayCount);
        const moreCount = employees.length - displayCount;

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {displayedEmployees.map((name, index) => (
                    <Chip
                        key={index}
                        label={truncateName(name)}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ fontSize: '0.7rem', height: '24px' }}
                    />
                ))}
                {moreCount > 0 && (
                    <Chip
                        label={`+${moreCount} more`}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: '24px', fontWeight: 'bold' }}
                    />
                )}
            </Box>
        );
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Goals History</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="contained" color="primary" startIcon={<CloudUpload />} onClick={() => setOpenExcelDialog(true)} style={{ textTransform: 'capitalize' }}>
                        Add Goal using Excel
                    </Button>
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddHrGoal} style={{ textTransform: 'capitalize' }}>
                        Add Goal
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Goal Title</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Employees</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Created Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {goalsHistoryData.map((goal, index) => (
                                <TableRow key={index} hover>
                                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{goal.title}</TableCell>
                                    <TableCell>
                                        <EmployeeListDisplay employees={goal.employees} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={goal.status}
                                            size="small"
                                            sx={{
                                                backgroundColor: getStatusColor(goal.status) + '20',
                                                color: getStatusColor(goal.status),
                                                fontWeight: 700,
                                                fontSize: '0.7rem'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(goal.createdDate)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Button size="small" variant="outlined" sx={{ mr: 1 }} style={{ textTransform: 'capitalize' }}>View</Button>
                                        <Button size="small" variant="outlined" color="secondary" onClick={() => handleEditGoal(goal)} style={{ textTransform: 'capitalize' }}>Edit</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {goalsHistoryData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">No goals found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </TableContainer>
            )}

            {/* --- Modals and Dialogs --- */}

            {/* Create Goal Component */}
            <CreateGoal
                open={openAddGoal}
                onClose={() => setOpenAddGoal(false)}
                refreshData={fetchGoals}
                showSnackbar={showSnackbar}
            />

            {/* Edit Goal Component */}
            {editingGoal && (
                <EditGoal
                    open={openEditGoal}
                    onClose={() => { setOpenEditGoal(false); setEditingGoal(null); }}
                    goalData={editingGoal}
                    refreshData={fetchGoals}
                    showSnackbar={showSnackbar}
                />
            )}

            {/* Excel Upload Dialog */}
            <Dialog open={openExcelDialog} onClose={handleCloseExcelDialog}>
                <DialogTitle>Upload Goals Excel</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <input type="file" accept=".xlsx, .xls" onChange={handleExcelFileSelect} />
                        {excelFile && <Typography sx={{ mt: 1 }}>Selected: {excelFile.name}</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseExcelDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUploadExcel}
                        disabled={!excelFile || excelUploading}
                    >
                        {excelUploading ? <CircularProgress size={24} /> : "Upload"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default GoalManagement;